import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, ChevronLeft, Trash2, Upload, Film, Share2, Copy, Check, ExternalLink, Play, Layers,
  RefreshCw, Images, FileDown, CopyPlus, ArrowLeft, ArrowRight, X, Star, FolderOpen, MessageSquare,
} from 'lucide-react';
import { Card, Btn, Badge, Modal, Field, Input, Select, EmptyState } from '../../components/UI';
import {
  uploadImageFile, uploadCoverBlob, captureFrame, uploadBlob, listLibrary,
  removePaths, folderUsage, fmtBytes, mediaUrl, publicUrl, CONTENT_BUCKET, MOCKUP_BUCKET,
} from '../../utils/media';
import { LOGOS, logoUrl, logoForClientName, logoMeta } from '../../utils/brandLogos';
import { buildMockupHtml } from '../../utils/mockupHtml';
import { POST_TYPES, uid, nowIso, today, thisMonth, nextMonth, monthLabel, canMakeMockups, clientOptions, mockupFromCalendar, designerEmployees, empName, makeNotif, postNumber } from './contentShared';
import { sendPush } from '../../utils/push';
import { markMockupChange, setMockupCellStatus, markMockupPublished, setMockupCellFields, setMockupCells } from '../../utils/mockupChanges';
import { usePresence, PresenceBar } from '../../components/Presence';
import { useLiveCaptions } from '../../utils/useLiveField';
import { markLocalWrite } from '../../utils/db';

const FREE_LIMIT = 1024 * 1024 * 1024; // 1 GB free plan
const lbl = { fontSize: 10, fontWeight: 400, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4 };
const inp = { width: '100%', padding: '7px 9px', fontSize: 12.5, background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'var(--font)' };
const ta = { ...inp, resize: 'vertical', lineHeight: 1.45, minHeight: 50 };
const miniBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 9.5, fontWeight: 400, fontFamily: 'var(--mono)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '7px 8px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text2)', background: 'var(--bg3)' };

function emptyCell() {
  return { id: uid(), type: 'Post', coverPath: '', coverSource: 'designed', reelUrl: '', driveUrl: '', carouselPaths: [], caption: '', likes: '50 likes', date: '' };
}
function slugHandle(name) { return (name || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
// Every storage path referenced by a set of mockups (for reference-counted deletion).
function usedPaths(list) {
  const s = new Set();
  (list || []).forEach(mm => (mm.data?.cells || []).forEach(c => {
    if (c.coverPath) s.add(c.coverPath);
    (c.carouselPaths || []).forEach(p => s.add(p));
  }));
  return s;
}

export default function MockupStudio({ store, persist, showToast, session, seedCalendar, onSeedConsumed }) {
  const employees = store.employees || [];
  const clients = clientOptions(store.clients);
  const mockups = store.mockups || [];
  const canEdit = canMakeMockups(session, employees);
  const myId = session?.role === 'admin' ? 'admin' : session?.empId;

  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ clientId: '', monthYear: thisMonth(), title: '', rows: 3 });
  const [busy, setBusy] = useState({});
  const [framePick, setFramePick] = useState(null);
  const [libPick, setLibPick] = useState(null);     // {cellId, mode:'cover'|'carousel'}
  const [libItems, setLibItems] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [chDraft, setChDraft] = useState({});   // per client-change: { details, designerId }
  const [chBusy, setChBusy] = useState({});     // per client-change: RPC in flight
  const [hiddenChg, setHiddenChg] = useState({}); // optimistic hide after resolve/dismiss (until realtime reload)
  const [usage, setUsage] = useState(null);
  const [purgeBefore, setPurgeBefore] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const dragIdx = useRef(null);
  const seededRef = useRef(false);

  const selected = mockups.find(m => m.id === selectedId) || null;

  // Live presence — team members see who else has this mockup open / is editing.
  const presenceMe = { id: myId, name: empName(employees, myId) || 'Admin' };
  const { peers, setEditing } = usePresence(selectedId ? 'mockup:' + selectedId : null, presenceMe);
  useEffect(() => { setEditing(!!selectedId); }, [selectedId, setEditing]);

  // Live caption co-editing (Google-Sheets style): everyone watching a mockup sees a
  // caption update live as someone types, with a per-post "✍ … is editing" badge.
  // Runtime kill-switch: set app_config flags.liveCaptions=false to turn it off live
  // for everyone, no redeploy. Fail-safe: if off or realtime is down, captions behave
  // exactly as before (store-backed).
  const liveCaptionsOn = (store.flags?.liveCaptions !== false);
  const { sendCaption, remoteFor } = useLiveCaptions(
    liveCaptionsOn && selectedId ? 'mockup:' + selectedId : null, presenceMe);
  const [focusCell, setFocusCell] = useState(null);

  // Flush any pending atomic content write when leaving the mockup, hiding the tab, or
  // unloading — so the last caption/cover edit inside the debounce window is never lost.
  useEffect(() => {
    const onHide = () => { if (document.hidden) flushAllCells(); };
    window.addEventListener('beforeunload', flushAllCells);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('beforeunload', flushAllCells); document.removeEventListener('visibilitychange', onHide); flushAllCells(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  const clientsInList = Array.from(new Map(mockups.map(m => [m.clientId, m.clientName])).entries());
  const visible = mockups.filter(m => filterClient === 'all' || m.clientId === filterClient)
    .slice().sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));

  const refreshUsage = useCallback(() => { folderUsage(CONTENT_BUCKET).then(setUsage).catch(() => {}); }, []);
  useEffect(() => { if (!selectedId) refreshUsage(); }, [selectedId, refreshUsage]);

  useEffect(() => {
    if (!seedCalendar || seededRef.current) return;
    seededRef.current = true;
    // If the calendar already has a mockup (auto-created on approval, or built
    // before), open that one instead of making a duplicate.
    const existing = mockups.find(mm => mm.calendarId === seedCalendar.id);
    if (existing) { setSelectedId(existing.id); onSeedConsumed && onSeedConsumed(); return; }
    const m = mockupFromCalendar(seedCalendar, clients, myId);
    persist({ ...store, mockups: [...mockups, m] });
    setSelectedId(m.id); onSeedConsumed && onSeedConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedCalendar]);

  function saveMockup(updated, extra = {}, opts) {
    const u = { ...updated, updatedAt: nowIso() };
    persist({ ...store, mockups: mockups.map(m => m.id === u.id ? u : m), ...extra }, opts);
  }
  // Structural change to the cell list (add row / remove post / reorder). Paints
  // optimistically, then writes the list atomically via set_mockup_cells, which OWNS
  // membership and order. `opts` still carries the explicit deletedIds through to the
  // whole-store save, so both guards agree on what the user actually removed.
  function saveCells(m, cells, opts) {
    saveMockup({ ...m, data: { ...m.data, cells } }, {}, opts);
    setMockupCells(m.id, cells).catch(() => showToast('Layout didn\u2019t save \u2014 check your connection.', 'error'));
  }
  function patchData(m, patch) { saveMockup({ ...m, data: { ...m.data, ...patch } }); }
  // Content fields (cover/caption/link/reel/carousel) are written atomically per-cell
  // via set_mockup_cell_fields — the whole-store merge no longer touches them, so a
  // stale save can never revert them. Debounce the RPC (~500ms, matching the old
  // whole-store cadence) so rapid caption typing coalesces into ONE write instead of
  // one-per-keystroke; flush immediately on blur / leaving the mockup / tab hide.
  // Fields OWNED by the atomic per-cell write (set_mockup_cell_fields). These are written
  // straight to their own cell server-side and skipped by the whole-store save
  // (mockup_save_keep_feedback), so a stale/second tab can never revert them. `type`,
  // `likes` and `date` were added here (+ the RPC skip-list) to fix Reel→Post reverting.
  const CONTENT_FIELDS = ['caption', 'driveUrl', 'reelUrl', 'coverPath', 'coverSource', 'carouselPaths', 'type', 'likes', 'date'];
  // ── Typed content must never come back after it's deleted ────────────────────
  // Two things used to bring deleted caption text back, now and then:
  //  1. Each pause in typing sent its own write, so two could be in flight at once; on a
  //     slow connection the older one sometimes landed LAST and the database kept the
  //     earlier text. → Writes for a post now go one at a time: while one is in flight,
  //     newer edits wait and are sent (merged, latest wins) the moment it lands.
  //  2. A live reload could replace the store — and so the caption box — with the
  //     database's copy while your latest edit was still on its way, and anything typed
  //     next was typed on top of the older text. → What you typed is kept in `overlay`
  //     and shown in preference to the store until the database has caught up with it.
  const cellWrite = useRef({ pending: {}, timers: {}, inflight: {} });
  const [overlay, setOverlay] = useState({});        // 'mockId|cellId' → { fields, acked }
  const withOverlay = (mockId, list) => (list || []).map(c => { const o = overlay[mockId + '|' + c.id]; return o ? { ...c, ...o.fields } : c; });
  function sendCell(mockId, cellId) {
    const key = mockId + '|' + cellId;
    const st = cellWrite.current;
    if (st.inflight[key]) return;                   // the write in flight sends this when it lands
    const fields = st.pending[key];
    if (!fields || !Object.keys(fields).length) return;
    delete st.pending[key];
    st.inflight[key] = true;
    markLocalWrite();                               // hold live reloads until this settles
    setMockupCellFields(mockId, cellId, fields)
      .catch(() => {
        // Keep the edit: put it back underneath anything typed since, and try again.
        st.pending[key] = { ...fields, ...(st.pending[key] || {}) };
        showToast('That change didn’t save yet — retrying…', 'error');
        if (!st.timers[key]) st.timers[key] = setTimeout(() => { delete st.timers[key]; sendCell(mockId, cellId); }, 3000);
      })
      .finally(() => {
        st.inflight[key] = false;
        markLocalWrite();
        if (st.pending[key] && !st.timers[key]) sendCell(mockId, cellId);
        else if (!st.pending[key]) setOverlay(o => (o[key] ? { ...o, [key]: { ...o[key], acked: Date.now() } } : o));
      });
  }
  function flushCell(mockId, cellId) {
    const key = mockId + '|' + cellId;
    if (cellWrite.current.timers[key]) { clearTimeout(cellWrite.current.timers[key]); delete cellWrite.current.timers[key]; }
    sendCell(mockId, cellId);
  }
  function flushAllCells() { Object.keys(cellWrite.current.pending).forEach(key => { const i = key.indexOf('|'); flushCell(key.slice(0, i), key.slice(i + 1)); }); }
  function updateCell(m, cellId, patch) {
    // Optimistic UI + structural/metadata durability via the whole-store save.
    saveMockup({ ...m, data: { ...m.data, cells: (m.data.cells || []).map(c => c.id === cellId ? { ...c, ...patch } : c) } });
    const cp = {};
    for (const k of CONTENT_FIELDS) if (k in patch) cp[k] = patch[k];
    if (Object.keys(cp).length) {
      const key = m.id + '|' + cellId;
      setOverlay(o => ({ ...o, [key]: { fields: { ...((o[key] && o[key].fields) || {}), ...cp }, acked: 0 } }));
      cellWrite.current.pending[key] = { ...(cellWrite.current.pending[key] || {}), ...cp };
      if (cellWrite.current.timers[key]) clearTimeout(cellWrite.current.timers[key]);
      cellWrite.current.timers[key] = setTimeout(() => { delete cellWrite.current.timers[key]; sendCell(m.id, cellId); }, 500);
    }
  }
  // Drop an overlay once nothing is waiting to be written for that post AND the store
  // shows the same values (the database caught up) — or, failing that, a few seconds
  // after the last write was confirmed (someone else may have edited it since).
  useEffect(() => {
    setOverlay(o => {
      const keys = Object.keys(o);
      if (!keys.length) return o;
      const st = cellWrite.current; const next = {}; let changed = false;
      for (const key of keys) {
        const i = key.indexOf('|'); const mid = key.slice(0, i), cid = key.slice(i + 1);
        const busy = st.inflight[key] || st.pending[key] || st.timers[key];
        const cell = (((mockups.find(mm => mm.id === mid) || {}).data || {}).cells || []).find(c => c.id === cid);
        const caughtUp = cell && Object.keys(o[key].fields).every(f => JSON.stringify(cell[f] ?? '') === JSON.stringify(o[key].fields[f] ?? ''));
        if (!busy && (caughtUp || (o[key].acked && Date.now() - o[key].acked > 8000))) { changed = true; continue; }
        next[key] = o[key];
      }
      return changed ? next : o;
    });
  }, [store.mockups]); // eslint-disable-line react-hooks/exhaustive-deps
  // Manager manual override for a post's client status (atomic RPC — clientStatus is
  // never writable through the whole-store save). Used for caption-only fixes the CM
  // does themselves: mark the post purple ("change made · re-check"), or clear it.
  async function setCellStatus(m, cell, status) {
    try {
      await setMockupCellStatus(m.id, cell.id, status, { empId: myId });
      showToast(status === 'changed' ? 'Marked “change made · re-check”.' : 'Client status cleared.');
    } catch (e) { showToast('Could not update — ' + (e.message || 'please try again'), 'error'); }
  }

  // ── Client feedback → task ──────────────────────────────────────
  // A CM reviews one client change, assigns a designer, and it becomes an item
  // under the mockup's single "Mockup changes — [Month]" task. The change's
  // lifecycle (resolved → submitted → done) lives ON THE MOCKUP and is written
  // ONLY through the atomic mark_mockup_change RPC — never the whole-store save —
  // so a concurrent client action can no longer clobber it (the vanish/reappear bug).
  const ARCHIVED = s => s === 'Reviewed' || s === 'Completed';

  // Run one atomic change action; optimistically hide the card until realtime reloads.
  async function runChangeRpc(mock, change, action, extra = {}, hide = false) {
    setChBusy(b => ({ ...b, [change.id]: true }));
    try {
      const res = await markMockupChange(mock.id, change.id, action, extra);
      if (hide) setHiddenChg(h => ({ ...h, [change.id]: true }));
      return res;
    } catch (e) {
      showToast('Could not save — ' + (e.message || 'please try again'), 'error');
      throw e;
    } finally {
      setChBusy(b => { const x = { ...b }; delete x[change.id]; return x; });
    }
  }

  async function resolveClientChange(mock, change) {
    const draft = chDraft[change.id] || {};
    const cellIndex = (mock.data.cells || []).findIndex(c => c.id === change.cellId);
    const details = (draft.details || '').trim();
    const designerId = draft.designerId || '';
    if (!designerId) { showToast('Pick a designer to assign this change to.', 'error'); return; }

    // Ensure the container task exists. It is created ONCE (a safe new-row add);
    // the per-change items are read live from the mockup, so we never fragile-append
    // subtasks (the pattern that scattered duplicate tasks before).
    let tasks = store.tasks || [];
    const existing = mock.data.changesTaskId ? tasks.find(t => t.id === mock.data.changesTaskId && !ARCHIVED(t.status)) : null;
    const changesTaskId = existing ? existing.id : uid();
    if (existing) {
      if (!(existing.assignedToIds || []).includes(designerId)) {
        const assignedToIds = Array.from(new Set([...(existing.assignedToIds || []), designerId]));
        tasks = tasks.map(t => t.id === existing.id ? { ...t, assignedToIds } : t);
      }
    } else {
      tasks = [...tasks, {
        id: changesTaskId, clientName: mock.clientName, mockupId: mock.id,
        taskName: `Mockup changes — ${monthLabel(mock.monthYear)}`, category: 'Mockup',
        assigneeId: designerId, assigneeName: empName(employees, designerId),
        assignedToIds: [designerId], dueDate: today(), status: 'Not Started',
        notes: `Client-requested changes on the ${monthLabel(mock.monthYear)} mockup for ${mock.clientName}. Each item is one post — the designer submits it, you approve it, then the client is told to re-check.`,
        hasSubTasks: false, subTasks: [], workingOn: [], completedBy: [],
        createdAt: nowIso(), createdBy: myId,
      }];
    }
    const notifs = [makeNotif(designerId, `Mockup change to do: ${mock.clientName} · Post #${postNumber(cellIndex, (mock.data.cells || []).length)}`, { id: changesTaskId, name: `Mockup changes — ${monthLabel(mock.monthYear)}`, client: mock.clientName })];
    // 1) task container (safe: a brand-new task is added, an existing one is only lightly touched)
    persist({ ...store, tasks, notifications: [...(store.notifications || []), ...notifs] });
    // 2) the authoritative change lifecycle — atomic on the mockup row
    try {
      await runChangeRpc(mock, change, 'resolve', { taskId: changesTaskId, subTaskId: change.id, assignedTo: designerId, details }, true);
      sendPush(designerId, 'Mockup change', `${mock.clientName} · Post #${postNumber(cellIndex, (mock.data.cells || []).length)}`, '/');
      setChDraft(d => { const x = { ...d }; delete x[change.id]; return x; });
      showToast(existing ? 'Added to the mockup changes task.' : 'Task created for the change.');
    } catch (e) { /* toast already shown; card stays for retry */ }
  }

  async function dismissClientChange(mock, change) {
    try {
      await runChangeRpc(mock, change, 'dismiss', {}, true);
      setChDraft(d => { const x = { ...d }; delete x[change.id]; return x; });
      showToast('Change dismissed.');
    } catch (e) { /* toast already shown */ }
  }

  function createMockup() {
    if (!newForm.clientId) return showToast('Pick a client.', 'error');
    const client = clients.find(c => c.id === newForm.clientId);
    const name = client?.name || '';
    const m = {
      id: uid(), clientId: newForm.clientId, clientName: name, monthYear: newForm.monthYear, calendarId: '',
      status: 'draft', createdAt: nowIso(), updatedAt: nowIso(), createdBy: myId,
      data: { title: newForm.title || `${monthLabel(newForm.monthYear)} Mockup`, handle: slugHandle(name), logoSlug: logoForClientName(name) || '', logoVariant: 'white', shareId: '', shareUrl: '', publishedAt: '', cells: Array.from({ length: Math.max(1, Number(newForm.rows) || 1) * 3 }, () => emptyCell()) },
    };
    persist({ ...store, mockups: [...mockups, m] });
    setShowNew(false); setNewForm({ clientId: '', monthYear: thisMonth(), title: '', rows: 3 });
    setSelectedId(m.id);
  }

  // Delete a mockup — remove only files not referenced by any OTHER mockup (shared library).
  async function deleteMockup(m) {
    if (!window.confirm('Delete this mockup? Its images are removed too — unless they’re used in another mockup.')) return;
    const keep = usedPaths(mockups.filter(x => x.id !== m.id));
    const toDelete = [...usedPaths([m])].filter(p => !keep.has(p));
    try { if (toDelete.length) await removePaths(CONTENT_BUCKET, toDelete); } catch (e) { /* non-fatal */ }
    if (m.data?.shareId) { try { await removePaths(MOCKUP_BUCKET, [`${m.data.shareId}.html`]); } catch (e) { /* */ } }
    persist({ ...store, mockups: mockups.filter(x => x.id !== m.id) }, { deletedIds: { mockups: [m.id] } });
    setSelectedId(null); showToast('Mockup deleted.'); refreshUsage();
  }

  // Duplicate — cells just reference the same library files (no re-upload); ref-counting keeps them safe.
  function duplicateMockup(m) {
    const my = nextMonth(m.monthYear);
    const cells = (m.data.cells || []).map(c => ({ ...c, id: uid() }));
    const copy = {
      id: uid(), clientId: m.clientId, clientName: m.clientName, monthYear: my, calendarId: '',
      status: 'draft', createdAt: nowIso(), updatedAt: nowIso(), createdBy: myId,
      data: { title: `${monthLabel(my)} Mockup`, handle: m.data.handle, logoSlug: m.data.logoSlug, logoVariant: m.data.logoVariant, shareId: '', shareUrl: '', publishedAt: '', cells },
    };
    persist({ ...store, mockups: [...mockups, copy] });
    setSelectedId(copy.id); showToast(`Duplicated to ${monthLabel(my)}.`);
  }

  async function purgeOld() {
    if (!purgeBefore) return;
    const doomed = mockups.filter(m => m.monthYear && m.monthYear < purgeBefore);
    if (!doomed.length) return showToast('No mockups before that month.', 'info');
    if (!window.confirm(`Delete ${doomed.length} mockup(s) before ${monthLabel(purgeBefore)}? This frees their storage.`)) return;
    const doomedIds = new Set(doomed.map(m => m.id));
    const keep = usedPaths(mockups.filter(m => !doomedIds.has(m.id)));
    const toDelete = [...usedPaths(doomed)].filter(p => !keep.has(p));
    try { if (toDelete.length) await removePaths(CONTENT_BUCKET, toDelete); } catch (e) { /* */ }
    for (const m of doomed) { if (m.data?.shareId) { try { await removePaths(MOCKUP_BUCKET, [`${m.data.shareId}.html`]); } catch (e) { /* */ } } }
    persist({ ...store, mockups: mockups.filter(m => !doomedIds.has(m.id)) }, { deletedIds: { mockups: [...doomedIds] } });
    showToast(`Purged ${doomed.length} mockup(s).`); refreshUsage();
  }

  // ── uploads → client library (reusable) ─────────────────────────
  async function onUploadCover(m, cell, file) {
    if (!file) return;
    setBusy(b => ({ ...b, [cell.id]: true }));
    try {
      const path = `${m.clientId}/lib/${uid()}.webp`;
      await uploadImageFile(path, file);
      updateCell(m, cell.id, { coverPath: path, coverSource: 'designed' });
      showToast('Cover uploaded.');
    } catch (e) { showToast('Upload failed: ' + (e.message || e), 'error'); }
    setBusy(b => ({ ...b, [cell.id]: false }));
  }
  async function onUploadCarousel(m, cell, files) {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(b => ({ ...b, [cell.id]: true }));
    try {
      const paths = [...(cell.carouselPaths || [])];
      for (const f of list) { const path = `${m.clientId}/lib/${uid()}.webp`; await uploadImageFile(path, f); paths.push(path); }
      updateCell(m, cell.id, { carouselPaths: paths, coverPath: paths[0], coverSource: 'designed' });
      showToast(`${list.length} slide(s) added.`);
    } catch (e) { showToast('Upload failed: ' + (e.message || e), 'error'); }
    setBusy(b => ({ ...b, [cell.id]: false }));
  }

  // ── media library picker ────────────────────────────────────────
  async function openLibrary(cell, mode) {
    setLibPick({ cellId: cell.id, mode });
    setLibLoading(true);
    try { setLibItems(await listLibrary(selected.clientId)); } catch (e) { setLibItems([]); }
    setLibLoading(false);
  }
  function pickFromLibrary(path) {
    const m = selected; const cell = (m.data.cells || []).find(c => c.id === libPick.cellId);
    if (!cell) { setLibPick(null); return; }
    if (libPick.mode === 'carousel') {
      const paths = [...(cell.carouselPaths || []), path];
      updateCell(m, cell.id, { carouselPaths: paths, coverPath: paths[0] });
    } else {
      updateCell(m, cell.id, { coverPath: path, coverSource: 'designed' });
    }
    setLibPick(null);
  }

  // ── frame from reel → library ───────────────────────────────────
  function openFramePicker(cell) { setFramePick({ cellId: cell.id, file: null, url: cell.reelUrl || '', time: 0, duration: 0 }); }
  async function captureFramePick() {
    const fp = framePick; const m = selected; const src = fp.file || fp.url;
    if (!src) return showToast('Choose a video file or paste a direct reel URL.', 'error');
    setBusy(b => ({ ...b, [fp.cellId]: true }));
    try {
      const blob = await captureFrame(src, fp.time);
      const path = `${m.clientId}/lib/${uid()}.webp`;
      await uploadCoverBlob(path, blob);
      updateCell(m, fp.cellId, { coverPath: path, coverSource: 'frame' });
      setFramePick(null); showToast('Cover captured from reel.');
    } catch (e) { showToast(e.message || 'Could not capture frame.', 'error'); }
    setBusy(b => ({ ...b, [fp.cellId]: false }));
  }

  // ── move / clear / carousel ordering ────────────────────────────
  function moveCell(m, i, dir) {
    const cells = [...(m.data.cells || [])]; const j = i + dir;
    if (j < 0 || j >= cells.length) return;
    [cells[i], cells[j]] = [cells[j], cells[i]];
    saveMockup({ ...m, data: { ...m.data, cells } });
  }
  function clearCover(m, cell) { updateCell(m, cell.id, { coverPath: '', carouselPaths: [] }); }
  function moveSlide(m, cell, idx, dir) {
    const paths = [...(cell.carouselPaths || [])]; const j = idx + dir;
    if (j < 0 || j >= paths.length) return;
    [paths[idx], paths[j]] = [paths[j], paths[idx]];
    updateCell(m, cell.id, { carouselPaths: paths, coverPath: paths[0] });
  }
  function removeSlide(m, cell, idx) {
    const paths = (cell.carouselPaths || []).filter((_, k) => k !== idx);
    updateCell(m, cell.id, { carouselPaths: paths, coverPath: paths[0] || '' });
  }
  function onDrop(m, toIdx) {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === toIdx) return;
    const cells = [...(m.data.cells || [])]; const [moved] = cells.splice(from, 1); cells.splice(toIdx, 0, moved);
    saveCells(m, cells);
  }

  async function publish(m) {
    setPubBusy(true);
    try {
      const shareId = m.data.shareId || ('m' + String(m.id).replace(/[^a-z0-9]/gi, '').slice(-8) + Math.random().toString(36).slice(2, 8));
      flushAllCells();                              // publish exactly what's on screen
      const cells = withOverlay(m.id, m.data.cells).map(c => ({
        type: c.type, caption: c.caption, date: c.date, likes: c.likes, reelUrl: c.reelUrl, driveUrl: c.driveUrl || '',
        coverUrl: c.coverPath ? mediaUrl(c.coverPath) : '',
        carouselUrls: (c.carouselPaths || []).map(p => mediaUrl(p)),
      }));
      const html = buildMockupHtml({ title: m.data.title, monthLabel: monthLabel(m.monthYear), handle: m.data.handle, logoUrl: m.data.logoSlug ? logoUrl(m.data.logoSlug, m.data.logoVariant) : '', cells });
      await uploadBlob(MOCKUP_BUCKET, `${shareId}.html`, new Blob([html], { type: 'text/html;charset=utf-8' }), 'text/html;charset=utf-8');
      const url = window.location.origin + '/m/' + shareId;
      // Stamp ONLY the share metadata, atomically — publishing must never rewrite the
      // posts. The old whole-mockup save wrote back a snapshot captured BEFORE this
      // upload, reverting any cover/caption edited during it. The badge/link refresh on
      // the next realtime sync (a re-publish already shows them).
      await markMockupPublished(m.id, shareId, url, nowIso());
      showToast('Published!');
    } catch (e) { showToast('Publish failed: ' + (e.message || e), 'error'); }
    setPubBusy(false);
  }

  // ═══════════════ LIST VIEW ═══════════════
  if (!selected) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 30, fontFamily: 'var(--serif)', fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--text)' }}>Mockups</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>Build interactive monthly grid mockups.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {clientsInList.length > 1 && (
              <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 170 }}>
                <option value="all">All clients</option>
                {clientsInList.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
            {canEdit && <Btn variant="primary" onClick={() => setShowNew(true)}><Plus size={15} />New mockup</Btn>}
          </div>
        </div>

        {usage != null && (
          <Card style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: 'var(--text2)' }}>Media storage · {fmtBytes(usage)} / {fmtBytes(FREE_LIMIT)}</span>
              {canEdit && <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="month" value={purgeBefore} onChange={e => setPurgeBefore(e.target.value)} style={{ ...inp, width: 150 }} />
                <Btn size="sm" variant="danger" onClick={purgeOld} disabled={!purgeBefore}>Purge older</Btn>
                <Btn size="sm" variant="ghost" onClick={refreshUsage}><RefreshCw size={13} /></Btn>
              </div>}
            </div>
            <div style={{ height: 6, borderRadius: 'var(--radius)', background: 'var(--bg4)', marginTop: 10, overflow: 'hidden' }}>
              <div className="bar-grow" style={{ height: '100%', width: `${Math.min(100, (usage / FREE_LIMIT) * 100)}%`, background: usage / FREE_LIMIT > 0.85 ? 'var(--red)' : 'var(--accent)' }} />
            </div>
          </Card>
        )}

        {visible.length === 0
          ? <Card><EmptyState message="No mockups yet." /></Card>
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 14 }}>
            {visible.map(m => {
              const cover = (m.data?.cells || []).find(c => c.coverPath);
              return (
                <Card key={m.id} hover style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
                  <div onClick={() => setSelectedId(m.id)}>
                    <div style={{ aspectRatio: '16/10', background: 'var(--bg4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {cover ? <img src={mediaUrl(cover.coverPath)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Images size={26} color="var(--text3)" />}
                    </div>
                    <div style={{ padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{m.data?.title || m.clientName}</div>
                        {m.data?.shareUrl && <Badge label="Published" color="green" />}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 2 }}>{m.clientName} · {monthLabel(m.monthYear)} · {(m.data?.cells || []).length} posts</div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>}

        <Modal open={showNew} onClose={() => setShowNew(false)} title="New mockup">
          <Field label="Client">
            <Select value={newForm.clientId} onChange={e => setNewForm(f => ({ ...f, clientId: e.target.value }))}>
              <option value="">Select a client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Month"><Input type="month" value={newForm.monthYear} onChange={e => setNewForm(f => ({ ...f, monthYear: e.target.value }))} /></Field>
          <Field label="Rows (3 posts each)" hint="You can add or remove rows anytime while editing."><Input type="number" min={1} max={30} value={newForm.rows} onChange={e => setNewForm(f => ({ ...f, rows: e.target.value }))} /></Field>
          <Field label="Title (optional)"><Input value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. July Mockup" /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={createMockup}>Create</Btn>
          </div>
        </Modal>
      </div>
    );
  }

  // ═══════════════ EDITOR VIEW ═══════════════
  const m = selected;
  const cells = withOverlay(m.id, m.data.cells);     // what you typed wins until saved
  const lm = logoMeta(m.data.logoSlug);

  return (
    <div>
      <button onClick={() => setSelectedId(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
        <ChevronLeft size={16} />All mockups
      </button>
      {peers.length > 1 && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}><PresenceBar users={peers} meId={myId} /></div>}

      <Card style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          {m.data.logoSlug
            ? <img src={logoUrl(m.data.logoSlug, m.data.logoVariant)} alt="" style={{ maxHeight: 56, maxWidth: 220, objectFit: 'contain', background: m.data.logoVariant === 'white' ? '#1f1a12' : 'transparent', padding: m.data.logoVariant === 'white' ? '6px 12px' : 0, borderRadius: 'var(--radius)' }} />
            : <div style={{ fontFamily: 'var(--serif)', fontSize: 24, color: 'var(--text)' }}>{m.clientName}</div>}
          <div style={{ marginTop: 6, fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 400, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--accent)' }}>{monthLabel(m.monthYear)} Mockup</div>
          {m.data.title && <div style={{ marginTop: 5, fontSize: 15, fontWeight: 400, fontFamily: 'var(--serif)', color: 'var(--text2)' }}>{m.data.title}</div>}
        </div>
        {canEdit && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
            <div><div style={lbl}>Title (the mockup's name)</div><input style={inp} value={m.data.title || ''} onChange={e => patchData(m, { title: e.target.value })} placeholder={`${monthLabel(m.monthYear)} Mockup`} /></div>
            <div><div style={lbl}>Handle</div><input style={inp} value={m.data.handle} onChange={e => patchData(m, { handle: e.target.value })} /></div>
            <div><div style={lbl}>Brand logo</div>
              <select style={inp} value={m.data.logoSlug} onChange={e => patchData(m, { logoSlug: e.target.value })}>
                <option value="">— none —</option>
                {LOGOS.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
              </select></div>
            <div><div style={lbl}>Logo colour</div>
              <select style={inp} value={m.data.logoVariant} onChange={e => patchData(m, { logoVariant: e.target.value })}>
                {(lm?.variants || ['black', 'white']).map(v => <option key={v} value={v}>{v}</option>)}
              </select></div>
          </div>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {canEdit && <Btn variant="secondary" disabled={pubBusy} onClick={() => publish(m)}><Share2 size={14} />{pubBusy ? 'Publishing…' : (m.data.shareUrl ? 'Re-publish' : 'Publish & share')}</Btn>}
        {m.data.shareUrl && <>
          <a href={m.data.shareUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--accent2)', fontWeight: 600, wordBreak: 'break-all' }}>{m.data.shareUrl}</a>
          <Btn size="sm" onClick={() => { navigator.clipboard.writeText(m.data.shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <Check size={12} /> : <Copy size={12} />}{copied ? 'Copied' : 'Copy'}</Btn>
          <Btn size="sm" variant="ghost" onClick={() => window.open(m.data.shareUrl, '_blank')}><ExternalLink size={12} />Open</Btn>
          {m.data.shareId && <Btn size="sm" variant="ghost" onClick={() => window.open(publicUrl(MOCKUP_BUCKET, m.data.shareId + '.html') + '?print=1', '_blank')} title="Opens the printable gallery ready to save as PDF (all on one page)"><FileDown size={12} />PDF</Btn>}
        </>}
        <div style={{ flex: 1 }} />
        {canEdit && <Btn variant="ghost" onClick={() => duplicateMockup(m)} title="Duplicate to next month"><CopyPlus size={14} />Duplicate</Btn>}
        {canEdit && <Btn variant="danger" onClick={() => deleteMockup(m)}><Trash2 size={14} />Delete</Btn>}
      </div>

      {/* Client feedback — approvals + change requests from the share link */}
      {(() => {
        if (!canEdit) return null;
        const allCh = m.data.clientChanges || [];
        const toReview = allCh.filter(c => !c.resolved && !hiddenChg[c.id]);
        const inProgress = allCh.filter(c => c.resolved && !c.done && !c.dismissed);
        if (!toReview.length && !inProgress.length) return null;
        return (
        <Card style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <MessageSquare size={16} color="var(--accent)" />
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>Client feedback</span>
            {toReview.length > 0 && <Badge label={`${toReview.length} to review`} color="orange" />}
            {inProgress.length > 0 && <Badge label={`${inProgress.length} in progress`} color="blue" />}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {toReview.map(change => {
              const ci = (m.data.cells || []).findIndex(c => c.id === change.cellId);
              const draft = chDraft[change.id] || {};
              const cbusy = !!chBusy[change.id];
              return (
                <div key={change.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, background: 'var(--bg3)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Post #{postNumber(ci, (m.data.cells || []).length)} · {change.kind === 'move' ? 'Client reordered this post' : 'Change requested'}</div>
                  {change.kind === 'edit' && change.note && <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8, fontStyle: 'italic' }}>“{change.note}”</div>}
                  {change.kind === 'move' && <div style={{ fontSize: 12.5, color: 'var(--text3)', marginBottom: 8 }}>Client moved it to position #{postNumber(change.toIndex ?? 0, (m.data.cells || []).length)}.</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 8, marginBottom: 8 }} className="mk-fb-row">
                    <input style={inp} placeholder="Add details for the designer (optional)…" value={draft.details || ''} onChange={e => setChDraft(d => ({ ...d, [change.id]: { ...d[change.id], details: e.target.value } }))} />
                    <select style={inp} value={draft.designerId || ''} onChange={e => setChDraft(d => ({ ...d, [change.id]: { ...d[change.id], designerId: e.target.value } }))}>
                      <option value="">Assign designer…</option>
                      {designerEmployees(employees).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" variant="primary" disabled={cbusy} onClick={() => resolveClientChange(m, change)}>{cbusy ? 'Saving…' : 'Make task'}</Btn>
                    <Btn size="sm" variant="ghost" disabled={cbusy} onClick={() => dismissClientChange(m, change)}>Dismiss</Btn>
                  </div>
                </div>
              );
            })}
            {inProgress.length > 0 && (
              <div style={{ marginTop: toReview.length ? 6 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 400, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>In progress · approve on the Task board</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {inProgress.map(change => {
                    const ci = (m.data.cells || []).findIndex(c => c.id === change.cellId);
                    return (
                      <div key={change.id} style={{ display: 'flex', gap: 8, alignItems: 'center', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 10px', background: 'var(--bg2)' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: 'var(--text2)', flexShrink: 0 }}>Post #{postNumber(ci, (m.data.cells || []).length)}</span>
                        <span style={{ fontSize: 12, color: 'var(--text3)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{change.note || 'Change'}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{change.assignedTo ? empName(employees, change.assignedTo) : '—'}</span>
                        <Badge label={change.submitted ? 'Submitted' : change.started ? 'Working on it' : 'Assigned'} color={change.submitted ? 'purple' : change.started ? 'blue' : 'gray'} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>
        );
      })()}

      {/* Grid — exactly 3 per row, like Instagram */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
        {cells.map((cell, i) => (
          <Card key={cell.id} style={{ padding: 0, overflow: 'hidden',
              // Owner's colours: change requested = yellow, change made = blue.
              ...(cell.clientStatus === 'approved' ? { border: '1px solid rgba(169,137,83,0.55)' }
                : cell.clientStatus === 'changed' ? { border: '1px solid rgba(111,168,220,0.7)' }
                : (cell.clientStatus === 'edit' || cell.clientStatus === 'requested') ? { border: '1px solid rgba(226,192,68,0.65)' } : {}),
              transition: 'border-color .5s ease, box-shadow .5s ease' }}
            draggable={canEdit} onDragStart={() => { dragIdx.current = i; }} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(m, i)}>
            <div style={{ position: 'relative', aspectRatio: '4/5', background: 'var(--bg4)' }}>
              {cell.coverPath
                ? <img src={mediaUrl(cell.coverPath)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{cell.type}</div>}
              {/* STR flood: the cover washes gold on approval, terracotta when changes are requested */}
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', transition: 'opacity .5s ease', opacity: cell.clientStatus === 'approved' ? 1 : 0, background: 'linear-gradient(160deg, rgba(191,160,103,0.30), rgba(147,119,65,0.12) 60%, rgba(169,137,83,0.05))', mixBlendMode: 'overlay' }} />
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', transition: 'opacity .5s ease', opacity: (cell.clientStatus === 'edit' || cell.clientStatus === 'requested') ? 1 : 0, background: 'linear-gradient(160deg, rgba(226,192,68,0.30), rgba(180,150,50,0.12) 60%, rgba(226,192,68,0.05))', mixBlendMode: 'overlay' }} />
              <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 9.5, fontWeight: 400, fontFamily: 'var(--mono)', letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 'var(--radius)' }}>#{postNumber(i, cells.length)}</div>
              {cell.clientStatus && <div style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 9, fontWeight: 400, fontFamily: 'var(--mono)', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 8px', borderRadius: 'var(--radius)', background: cell.clientStatus === 'approved' ? 'var(--accent)' : cell.clientStatus === 'changed' ? '#6FA8DC' : '#E2C044', color: '#0A0A0C' }}>{cell.clientStatus === 'approved' ? 'Client approved' : cell.clientStatus === 'changed' ? 'Change made · re-check' : 'Change requested'}</div>}
              {cell.awaitingClient && <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6, background: 'rgba(199,154,91,0.95)', color: '#0A0A0C', fontSize: 9, fontWeight: 400, fontFamily: 'var(--mono)', textAlign: 'center', padding: '4px 4px', borderRadius: 'var(--radius)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Awaiting client footage</div>}
              {cell.type === 'Reel' && <div style={{ position: 'absolute', top: 8, right: 8, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.5))' }}><Play size={16} fill="#fff" /></div>}
              {cell.type === 'Carousel' && <div style={{ position: 'absolute', top: 8, right: 8, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.5))' }}><Layers size={16} /></div>}
              {canEdit && cell.coverPath && <button onClick={() => clearCover(m, cell)} title="Remove cover (keeps the file in your library)" style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', borderRadius: 'var(--radius)', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} /></button>}
              {busy[cell.id] && <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,10,12,.72)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>Uploading…</div>}
            </div>

            {canEdit && (
              <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* move + type */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => moveCell(m, i, -1)} disabled={i === 0} title="Move earlier" style={{ ...miniBtn, padding: 6, opacity: i === 0 ? 0.4 : 1 }}><ArrowLeft size={14} /></button>
                  <button onClick={() => moveCell(m, i, 1)} disabled={i === cells.length - 1} title="Move later" style={{ ...miniBtn, padding: 6, opacity: i === cells.length - 1 ? 0.4 : 1 }}><ArrowRight size={14} /></button>
                  <select style={{ ...inp, flex: 1 }} value={cell.type} onChange={e => updateCell(m, cell.id, { type: e.target.value })}>
                    {POST_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>

                {/* cover controls */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <label>
                    <span style={{ ...miniBtn, width: '100%' }}><Upload size={12} />Upload</span>
                    <input type="file" accept="image/*" hidden onChange={e => onUploadCover(m, cell, e.target.files[0])} />
                  </label>
                  <button onClick={() => openLibrary(cell, 'cover')} style={{ ...miniBtn, width: '100%' }}><FolderOpen size={12} />Library</button>
                  {cell.type === 'Reel' && <button onClick={() => openFramePicker(cell)} style={{ ...miniBtn, width: '100%' }}><Film size={12} />From reel</button>}
                  {cell.type === 'Carousel' && <>
                    <label>
                      <span style={{ ...miniBtn, width: '100%' }}><Layers size={12} />Add slides</span>
                      <input type="file" accept="image/*" multiple hidden onChange={e => onUploadCarousel(m, cell, e.target.files)} />
                    </label>
                    <button onClick={() => openLibrary(cell, 'carousel')} style={{ ...miniBtn, width: '100%' }}><FolderOpen size={12} />Lib slides</button>
                  </>}
                </div>

                {/* carousel slide ordering */}
                {cell.type === 'Carousel' && (cell.carouselPaths || []).length > 0 && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 6 }}>
                    <div style={{ ...lbl, marginBottom: 6 }}>Carousel order (1st = cover)</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {cell.carouselPaths.map((p, si) => (
                        <div key={p + si} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <img src={mediaUrl(p)} alt="" style={{ width: 34, height: 42, objectFit: 'cover', borderRadius: 5, flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: si === 0 ? 'var(--accent2)' : 'var(--text3)', width: 44, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{si === 0 ? <><Star size={11} />Cover</> : `#${si + 1}`}</span>
                          <div style={{ flex: 1 }} />
                          <button onClick={() => moveSlide(m, cell, si, -1)} disabled={si === 0} title="Up" style={{ ...miniBtn, padding: 5, opacity: si === 0 ? 0.4 : 1 }}><ArrowLeft size={12} style={{ transform: 'rotate(90deg)' }} /></button>
                          <button onClick={() => moveSlide(m, cell, si, 1)} disabled={si === cell.carouselPaths.length - 1} title="Down" style={{ ...miniBtn, padding: 5, opacity: si === cell.carouselPaths.length - 1 ? 0.4 : 1 }}><ArrowRight size={12} style={{ transform: 'rotate(90deg)' }} /></button>
                          <button onClick={() => removeSlide(m, cell, si)} title="Remove slide" style={{ ...miniBtn, padding: 5, color: 'var(--red)' }}><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cell.type === 'Reel' && <input style={inp} value={cell.reelUrl} placeholder="Reel link (to play in share view)" onChange={e => updateCell(m, cell.id, { reelUrl: e.target.value })} />}
                <input style={inp} value={cell.driveUrl || ''} placeholder="Master file link (Drive/Canva — client can open the high-res)" onChange={e => updateCell(m, cell.id, { driveUrl: e.target.value })} />
                {(() => {
                  // Live view: if someone ELSE is typing this caption and I'm not in the
                  // box, show their in-progress text; otherwise my normal store value.
                  const rc = (liveCaptionsOn && focusCell !== cell.id) ? remoteFor(cell.id) : null;
                  return (<>
                    <textarea style={ta} value={rc ? rc.value : cell.caption} placeholder="Caption…"
                      onFocus={() => setFocusCell(cell.id)}
                      onBlur={() => { setFocusCell(f => (f === cell.id ? null : f)); flushCell(m.id, cell.id); }}
                      onChange={e => { updateCell(m, cell.id, { caption: e.target.value }); if (liveCaptionsOn) sendCaption(cell.id, e.target.value); }} />
                    {rc && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent2)', marginTop: 2 }}>✍ {rc.name} is editing this caption…</div>}
                  </>);
                })()}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...inp, flex: 1 }} value={cell.date} placeholder="Date" onChange={e => updateCell(m, cell.id, { date: e.target.value })} />
                  <input style={{ ...inp, flex: 1 }} value={cell.likes} placeholder="Likes" onChange={e => updateCell(m, cell.id, { likes: e.target.value })} />
                </div>
                {/* Manual client-status override — for caption-only fixes the CM makes themselves */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Client status:</span>
                  <button onClick={() => setCellStatus(m, cell, 'changed')} style={{ ...miniBtn, color: 'var(--accent)' }} title="Mark this post as changed so the client re-checks it — use for caption or minor fixes you did yourself"><Check size={12} />Change made · re-check</button>
                  {cell.clientStatus && <button onClick={() => setCellStatus(m, cell, '')} style={{ ...miniBtn }} title="Clear the client status on this post">Clear status</button>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                  {cell.awaitingClient ? (<>
                    <input style={inp} value={cell.awaitingNote || ''} placeholder="What footage is needed from the client…" onChange={e => updateCell(m, cell.id, { awaitingNote: e.target.value })} />
                    <button onClick={() => updateCell(m, cell.id, { awaitingClient: false, awaitingNote: '' })} style={{ ...miniBtn, color: '#8FB08A' }}>Footage received — include in approval</button>
                  </>) : (
                    <button onClick={() => updateCell(m, cell.id, { awaitingClient: true })} style={{ ...miniBtn, color: '#C79A5B' }}>Mark awaiting client footage</button>
                  )}
                </div>
                <button onClick={() => saveCells(m, cells.filter(c => c.id !== cell.id), { deletedIds: { mockupCells: { [m.id]: [cell.id] } } })} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: 4 }}><Trash2 size={13} />Remove post</button>
              </div>
            )}
            {!canEdit && (() => {
              // A designer sees action controls ONLY on the specific posts whose
              // change was assigned to them and isn't finished yet. Everything else
              // stays a read-only preview.
              const myChanges = (m.data.clientChanges || []).filter(ch =>
                ch.cellId === cell.id && ch.resolved && !ch.done && !ch.dismissed && ch.assignedTo === myId);
              return (
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.4 }}><strong>{m.data.handle}</strong> {cell.caption}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{cell.date}</div>
                {myChanges.map(change => {
                  const cbusy = !!chBusy[change.id];
                  const link = (cell.driveUrl || '').trim();
                  return (
                    <div key={change.id} style={{ marginTop: 10, border: '1px solid var(--accent2)', borderRadius: 'var(--radius)', padding: 10, background: 'var(--bg3)' }}>
                      <div style={{ fontSize: 11, fontWeight: 400, fontFamily: 'var(--mono)', color: 'var(--accent2)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MessageSquare size={13} />Your change to make
                      </div>
                      {change.note && <div style={{ fontSize: 12.5, color: 'var(--text2)', fontStyle: 'italic', marginBottom: 6 }}>“{change.note}”</div>}
                      {change.details && <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Manager: {change.details}</div>}
                      {/* replace the cover for this post */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                        <label>
                          <span style={{ ...miniBtn, width: '100%' }}><Upload size={12} />New cover</span>
                          <input type="file" accept="image/*" hidden onChange={e => onUploadCover(m, cell, e.target.files[0])} />
                        </label>
                        <button onClick={() => openLibrary(cell, 'cover')} style={{ ...miniBtn, width: '100%' }}><FolderOpen size={12} />Library</button>
                      </div>
                      {/* the per-post submission link — this is what the client opens */}
                      <input style={{ ...inp, marginBottom: 6 }} value={cell.driveUrl || ''} placeholder="Submission link for this post (Drive/Canva)" onChange={e => updateCell(m, cell.id, { driveUrl: e.target.value })} />
                      {!change.submitted ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          {!change.started
                            ? <Btn size="sm" variant="default" disabled={cbusy} onClick={() => runChangeRpc(m, change, 'start', { empId: myId }).then(() => showToast('Marked as in progress.')).catch(() => {})}>Working on it</Btn>
                            : <span style={{ fontSize: 9.5, fontWeight: 400, fontFamily: 'var(--mono)', letterSpacing: '0.1em', textTransform: 'uppercase', background: '#E7EAEC', color: '#0A0A0C', padding: '4px 9px', borderRadius: 'var(--radius)' }}>In progress{change.startedBy && change.startedBy !== myId ? ` · ${empName(employees, change.startedBy)}` : ''}</span>}
                          <Btn size="sm" variant="success" disabled={cbusy || !link} onClick={() => runChangeRpc(m, change, 'submit', { empId: myId, link }).then(() => showToast('Submitted for the manager to review.')).catch(() => {})}>{cbusy ? 'Saving…' : 'Submit this post'}</Btn>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Badge label="Submitted — with manager" color="purple" />
                          <button onClick={() => runChangeRpc(m, change, 'reopen', {}).then(() => showToast('Reopened — you can edit and resubmit.')).catch(() => {})} disabled={cbusy} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>Reopen</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              );
            })()}
          </Card>
        ))}
      </div>

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={() => saveCells(m, [...cells, emptyCell(), emptyCell(), emptyCell()])}><Plus size={15} />Add row</Btn>
          <Btn variant="ghost" onClick={() => saveMockup({ ...m, data: { ...m.data, cells: [...cells, emptyCell()] } })}><Plus size={15} />Add post</Btn>
        </div>
      )}

      {/* Frame picker */}
      <Modal open={!!framePick} onClose={() => setFramePick(null)} title="Pick a cover frame from the reel" width={460}>
        {framePick && <>
          <Field label="Reel video" hint="Pick the reel file from your computer (best), or paste a direct video link.">
            <input type="file" accept="video/*" onChange={e => { const f = e.target.files[0]; if (f) setFramePick(fp => ({ ...fp, file: f, url: URL.createObjectURL(f) })); }} style={{ fontSize: 12, marginBottom: 6 }} />
            <input style={inp} value={framePick.file ? '(local file selected)' : framePick.url} placeholder="…or a direct .mp4/.webm link" disabled={!!framePick.file} onChange={e => setFramePick(fp => ({ ...fp, url: e.target.value }))} />
          </Field>
          {framePick.url && (
            <div>
              <video src={framePick.url} controls style={{ width: '100%', borderRadius: 'var(--radius)', background: '#000', maxHeight: 300 }}
                onLoadedMetadata={e => setFramePick(fp => ({ ...fp, duration: e.target.duration }))}
                onTimeUpdate={e => setFramePick(fp => ({ ...fp, time: e.target.currentTime }))} />
              <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 6 }}>Scrub the video to the frame you want, then capture. (Current: {framePick.time.toFixed(1)}s)</div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <Btn onClick={() => setFramePick(null)}>Cancel</Btn>
            <Btn variant="primary" disabled={!framePick.url} onClick={captureFramePick}><Film size={14} />Capture this frame</Btn>
          </div>
        </>}
      </Modal>

      {/* Media library picker */}
      <Modal open={!!libPick} onClose={() => setLibPick(null)} title={libPick?.mode === 'carousel' ? 'Add a slide from the library' : 'Pick a cover from the library'} width={560}>
        {libLoading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading library…</div>
          : libItems.length === 0 ? <EmptyState message="Nothing in this client's library yet. Upload a cover and it'll appear here to reuse." />
            : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(96px,1fr))', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
              {libItems.map(it => (
                <button key={it.path} onClick={() => pickFromLibrary(it.path)} title="Use this image" style={{ padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer', background: 'var(--bg3)', aspectRatio: '4/5' }}>
                  <img src={it.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
              ))}
            </div>}
      </Modal>
    </div>
  );
}
